import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import type { BackgroundProcessingError } from "@/app/services/generated/backgroundprocessingerrorService";
import {
  BackgroundProcessingErrorFilter,
  backgroundProcessingErrorService,
} from "@/app/services/generated/backgroundprocessingerrorService";
import type { EnumIntegrationType } from "@/app/services/generated/enums/enumIntegrationType.generated";
import type Guid from "@/app/types/common/guid";
import { translate, keys } from "@/plugins/i18n";
import appStore from "@/store/modules/appStore";
import { shallowRef, onMounted } from "vue";

export type BackgroundProcessingErrorKey = {
  integrationType: EnumIntegrationType;
  contextId: Guid | null;
};

export async function getBackgroundProcessingErrors(
  integrationErrorKeys: BackgroundProcessingErrorKey[],
  secondsAgo?: number,
  maximumResults?: number
) {
  const fromTimestamp = tenantService.now.minusSeconds(secondsAgo ?? 1800);

  const wooCommerceErrors = (
    await Promise.all(
      integrationErrorKeys
        .filter((x) => x.integrationType.isWooCommerce() && x.contextId)
        .map((x) =>
          backgroundProcessingErrorService.getWooCommerceErrors(
            x.contextId!,
            maximumResults ?? 10,
            fromTimestamp
          )
        )
    )
  ).flat();

  const integrateOnceErrors = (
    await Promise.all(
      integrationErrorKeys
        .filter((x) => x.integrationType.isIntegrateOnce() && x.contextId)
        .map((x) =>
          backgroundProcessingErrorService.getIntegrateOnceErrors(
            x.contextId!,
            maximumResults ?? 10,
            fromTimestamp
          )
        )
    )
  ).flat();

  const backgroundFilter = new BackgroundProcessingErrorFilter();
  backgroundFilter.integrationTypes = integrationErrorKeys
    .filter(
      (x) =>
        !x.integrationType.isIntegrateOnce() &&
        !x.integrationType.isWooCommerce()
    )
    .map((x) => x.integrationType);
  backgroundFilter.fromTimestamp = fromTimestamp;
  backgroundFilter.maximumResults = maximumResults ?? 10;

  const allOtherIntegrationErrors = await backgroundProcessingErrorService
    .getBackgroundProcessingErrorsByFilter(backgroundFilter)
    .then((x) => x.backgroundProcessingErrors);

  return wooCommerceErrors
    .concat(integrateOnceErrors)
    .concat(allOtherIntegrationErrors)
    .sort(
      (error1, error2) =>
        error2.timestamp.toEpochMilli() - error1.timestamp.toEpochMilli()
    );
}

export function useBackgroundProcessingErrorChecker(
  integrationType: EnumIntegrationType
) {
  const backgroundProcessingErrors = shallowRef<
    readonly BackgroundProcessingError[]
  >([]);

  const refreshBackgroundProcessingErrors = async (contextId: Guid | null) => {
    backgroundProcessingErrors.value = await getBackgroundProcessingErrors([
      { integrationType: integrationType, contextId: contextId },
    ]);
  };

  onMounted(async () => await refreshBackgroundProcessingErrors(null));

  return {
    backgroundProcessingErrors,
    refreshBackgroundProcessingErrors,
  };
}

export const showBackgroundImportStartedSnackbar = (
  integrationName: string
) => {
  appStore.pushSnackbarConfig({
    text: translate(keys.integrations_import_scheduled, {
      integrationName,
    }).toString(),
    timeout: 5000,
    position: "bottom",
  });
};

export const showBackgroundUpdaterStartedSnackbar = (
  integrationName: string
) => {
  appStore.pushSnackbarConfig({
    text: translate(keys.integrations_update_scheduled, {
      integrationName,
    }).toString(),
    timeout: 5000,
    position: "top",
  });
};
